// * Toast for Notification
import { toast } from 'react-toastify'
// * Constants helpers
import { AUTH_TOKEN, CSRF_TOKEN } from '../constants/constant'
// * APIs

import jwt_decode from 'jwt-decode'

export default function validateEmail(email) {
  // eslint-disable-next-line no-useless-escape
  const emailReg = /^([\w-\.]+@([\w-]+\.)+[\w-]{2,4})?$/
  return emailReg.test(email)
}

export const setCookie = (name, value, days) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  
  let domainStr = "";
  if (typeof window !== "undefined" && window.location.hostname.includes("khelouk.in")) {
    domainStr = "domain=.khelouk.in; ";
  }

  // Do not encodeURIComponent so that JSON strings remain readable as {"token":...}
  document.cookie = name + "=" + (value || "") + expires + "; path=/; " + domainStr + "SameSite=Lax";
};

export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const raw = c.substring(nameEQ.length, c.length);
      // Decode URL-encoded legacy cookies (e.g. %7B%22token%22...)
      try {
        return decodeURIComponent(raw);
      } catch (e) {
        return raw;
      }
    }
  }
  return null;
};

export const removeCookie = (name) => {
  // First, remove the cookie on the exact current hostname (legacy fallback)
  document.cookie = name + "=; Max-Age=-99999999; path=/;";
  
  // Then, remove it across the wildcard domain
  if (typeof window !== "undefined" && window.location.hostname.includes("khelouk.in")) {
    document.cookie = name + "=; Max-Age=-99999999; path=/; domain=.khelouk.in;";
  }
};

// ** Auth Token Helpers
export const getToken = () => {
  let token = getCookie(AUTH_TOKEN);
  if (!token) return null;

  // Backward compatibility for old JSON format
  try {
    const parsed = JSON.parse(token);
    if (parsed && parsed.token) {
      token = parsed.token;
    }
  } catch (e) {
    // Ignore, token is already a raw string
  }

  try {
    const decodedToken = jwt_decode(token);
    let currentDate = new Date();
    // JWT exp is in seconds
    if (decodedToken.exp * 1000 < currentDate.getTime()) {
      return null;
    }
    return token;
  } catch (error) {
    return null;
  }
};

export const setToken = (token) => {
  if (token) {
    setCookie(AUTH_TOKEN, token, 1); // 1 day
  }
};

export const removeToken = () => {
  removeCookie(AUTH_TOKEN);
};

// ** CSRF Token Helpers
export const setCSRFToken = (token) => {
  if (token) {
    localStorage.setItem(CSRF_TOKEN, token)
  }
}

export const getCSRFToken = () => {
  return localStorage.getItem(CSRF_TOKEN)
}

export const removeCSRFToken = () => {
  localStorage.removeItem(CSRF_TOKEN)
}

// * Function to validate the image
export const validateImage = (image, options = {}) => {
  const {
    maxSizeMB = 2,
    minWidth = 400,
    minHeight = 300,
    maxWidth = 4000,
    maxHeight = 4000,
    minAspect = 0.8, // allow portrait-ish to landscape-ish
    maxAspect = 2.5,
  } = options

  return new Promise((resolve) => {
    if (!image) return resolve(false)

    const allowedTypes = ['image/jpeg', 'image/jpg']
    if (!allowedTypes.includes(image.type)) {
      toast.error('Only jpeg / jpg formats are allowed', {
        position: toast.POSITION.TOP_RIGHT,
      })
      return resolve(false)
    }

    if (image.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Image must be smaller than ${maxSizeMB} MB`, {
        position: toast.POSITION.TOP_RIGHT,
      })
      return resolve(false)
    }

    // Check actual dimensions
    const url = URL.createObjectURL(image)
    const img = new Image()
    img.onload = () => {
      const w = img.width
      const h = img.height
      URL.revokeObjectURL(url)

      if (w < minWidth || h < minHeight) {
        toast.error(`Image dimensions should be at least ${minWidth}x${minHeight}px`, {
          position: toast.POSITION.TOP_RIGHT,
        })
        return resolve(false)
      }

      if (w > maxWidth || h > maxHeight) {
        toast.error(`Image dimensions should not exceed ${maxWidth}x${maxHeight}px`, {
          position: toast.POSITION.TOP_RIGHT,
        })
        return resolve(false)
      }

      const aspect = w / h
      if (aspect < minAspect || aspect > maxAspect) {
        toast.error('Image aspect ratio is not allowed. Please upload a properly proportioned image.', {
          position: toast.POSITION.TOP_RIGHT,
        })
        return resolve(false)
      }

      return resolve(true)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      toast.error('Could not read image file. Please try another image.', {
        position: toast.POSITION.TOP_RIGHT,
      })
      return resolve(false)
    }

    img.src = url
  })
}

// * Function to create the form data (Used in Add News , Edit News APIs)
export function multiPartData(data, type = '') {
  let multiPart = new FormData()
  for (let key in data) {
    if (type === 'multiple' && key === 'blogImage') {
      data[key].forEach((file) => {
        multiPart.append(key, file)
      })
    } else if (key !== 'blogImage') {
      multiPart.append(key, data[key])
    }
  }

  return multiPart
}

// * Function to Format the data
// * 👇️👇️👇️ Format Date as yyyy-mm-dd hh:mm:ss
export function formatDate(date) {
  return [date.getFullYear(), padTo2Digits(date.getMonth() + 1), padTo2Digits(date.getDate())].join(
    '-',
  )
}

// 👇️ (Helper functions)
function padTo2Digits(num) {
  return num.toString().padStart(2, '0')
}
